import { useEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { Hub } from "aws-amplify";

import { getAuthToken } from "app/utils/auth";

import type { HubCapsule } from "@aws-amplify/core";

/**
 * Resolves the Cognito access token and keeps it fresh by subscribing to
 * Amplify's `Hub` `auth` channel. On `tokenRefresh` the JWT is re-fetched
 * and propagated downstream — `useJun0Auth` re-POSTs `/auth/header` to
 * mint a new access_token cookie, which causes `useChat` to reconnect the
 * socket. The user keeps a live chat across the ~hourly Cognito rotation
 * with no page reload. On `signOut`, drops the token to null so consumers
 * see the unauthorized state.
 *
 * Pattern reference: `apps/console/src/bootstrap.ts:158` (the same Hub
 * channel powers app-wide redirect handling).
 */
export function useAuthToken(): string | null {
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      try {
        const t = await getAuthToken();
        if (!cancelled) setToken(t);
      } catch (err) {
        if (cancelled) return;
        Sentry.captureException(err, {
          tags: { feature: "jun0", step: "fetch-cognito-token" },
        });
      }
    }

    refresh();

    function handler(data: HubCapsule) {
      if (data.payload.event === "tokenRefresh") {
        refresh();
      } else if (data.payload.event === "signOut") {
        if (!cancelled) setToken(null);
      }
    }
    const unsubscribe = Hub.listen("auth", handler);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return token;
}
